import { useEffect, useRef, useState } from 'react';
import { api, type AdminRecording, type Version } from './api.ts';
import { formatWhen } from './duration.ts';

/**
 * WHERE A RECORDING IS IN THE HUMAN HALF OF THE PIPELINE.
 *
 * `recordings.status` answers what the MACHINE did — uploaded, queued,
 * processing, review, done, failed. It says nothing about the part an
 * administrator actually opens this tool for: has anybody worked on this, has
 * anybody handed their work over, and has the truth been picked. Those three
 * questions are answered by the VERSIONS of a recording, and this module is the
 * one place that turns a list of versions into that answer, so the Recordings
 * table, the Player's picker and the Versions cards cannot drift into three
 * different opinions about the same recording.
 *
 * ─────────────────────────────────────────────────────────────────────
 * THE RULE FOR "TRUTH PICKED", written down because it was a choice.
 *
 * The wire offers `status` ('main' is the truth so far) and `authorName`, where
 * NULL is the machine — "a real answer, not missing data", as the contract puts
 * it: the first pass belongs to nobody. So:
 *
 *     truth picked  ⇔  the version whose status is 'main' has an author
 *
 * i.e. a PERSON's arrangement is what the work queue now answers with, rather
 * than the machine's first run. Nothing but a promotion moves `main` — an
 * administrator's override or a vote quorum, both landing in the same place —
 * so a human-authored `main` is proof that somebody's version was chosen over
 * the machine's.
 *
 * THE OTHER READING WAS REJECTED. "Any version beat the machine" cannot be
 * computed from this payload: a branch exists the moment anybody edits one
 * clip, and an existing branch has not beaten anything until it is main.
 * Counting branches would put "truth picked" on every recording somebody merely
 * opened, which is the opposite of what the chip promises.
 *
 * ─────────────────────────────────────────────────────────────────────
 * WHY THIS FANS OUT ONE REQUEST PER RECORDING, AND WHY THAT IS BOUNDED.
 *
 * `GET /admin/recordings` carries no version information at all, and there is
 * no endpoint that summarises versions across a project — so the only honest
 * way to know whether a recording is waiting for review is to ask
 * `GET /versions?recordingId=…` about it. That is a request per row, so it is
 * capped (`MAX_LOOKUPS`), run a few at a time (`CONCURRENCY`), and asked only
 * about rows that CAN have versions: the pipeline writes segments and the first
 * version together, so a recording with no clips has no version either.
 *
 * The right fix is a `submittedCount`/`mainAuthor` pair on the recordings
 * listing itself, which is a server change and therefore not this workstream's
 * to make. Until then the cap is what keeps a 200-row project from opening 200
 * connections, and rows past it simply show no chip rather than a wrong one.
 */

/** At most this many recordings get a version lookup, newest first. */
const MAX_LOOKUPS = 60;

/** How many of those lookups are in flight at once. */
const CONCURRENCY = 5;

/* ------------------------------------------------------------------ */
/* Reading one version                                                 */
/* ------------------------------------------------------------------ */

/**
 * THE ONE PLACE THE STAMP IS READ, and the reason nothing downstream needs a
 * cast or a second opinion about it.
 *
 * Three ways to have no stamp, all of which happen: the key is absent (every
 * payload in production before the server carries it), the key is null (the
 * column exists and this branch was never submitted), or the key is a string no
 * `Date` can parse. The third is paranoia, and it is why this is a function
 * rather than `!!v.submittedAt` at four call sites — an unparseable stamp would
 * otherwise reach `formatWhen`, which answers '—' for anything it cannot read,
 * and the badge would say "submitted —".
 */
function stamp(v: Version): string | null {
  const at = v.submittedAt;
  if (typeof at !== 'string' || at === '' || Number.isNaN(Date.parse(at))) return null;
  return at;
}

/** Has this version been handed over? */
export function isSubmitted(v: Version): boolean {
  return stamp(v) !== null;
}

/**
 * "submitted 2h ago", or NULL when there is no stamp to show.
 *
 * Null and not '' so that a caller writing `{submittedNote(v) && <span>…}`
 * renders nothing at all — no empty pill, no stray separator.
 */
export function submittedNote(v: Version): string | null {
  const at = stamp(v);
  return at === null ? null : `submitted ${formatWhen(at)}`;
}

/** The same fact as a sentence, for a `title`. Names who, when there is a who —
 *  a submitted version is somebody's branch, and its `authorName` is that
 *  somebody. A machine version carrying a stamp would say only when. */
export function submittedTitle(v: Version): string | null {
  const at = stamp(v);
  if (at === null) return null;
  const when = formatWhen(at);
  return v.authorName ? `Submitted by ${v.authorName}, ${when}.` : `Submitted ${when}.`;
}

/* ------------------------------------------------------------------ */
/* Reading a recording's whole list                                    */
/* ------------------------------------------------------------------ */

/** Handed over and not yet crowned: the definition of "waiting for review". */
export function isWaiting(v: Version): boolean {
  return isSubmitted(v) && v.status !== 'main';
}

/** How many people are waiting on this recording. */
export function submittedCount(versions: readonly Version[]): number {
  return versions.filter(isWaiting).length;
}

/** Somebody handed something over and the truth has not moved to it: the one
 *  question the recording picker sorts by. Unknown is not "yes" — a lookup that
 *  has not answered, or was never made, must not push a row to the top. */
export function needsReview(facts: ReviewFacts | undefined): boolean {
  return facts !== undefined && facts.at === 'ready' && facts.versions.some(isWaiting);
}

/** See the header: `main` has an author ⇒ a person's arrangement is the truth. */
export function truthPicked(versions: readonly Version[]): boolean {
  const main = versions.find((v) => v.status === 'main');
  return main !== undefined && main.authorName !== null;
}

/**
 * The newest handover, or undefined. What the Player opens as the second lane:
 * "the truth, and the thing asking to replace it".
 *
 * Sorted by the STAMP and not by `createdAt`, because a branch made last week
 * and submitted this morning is the newer claim on an administrator's
 * attention.
 */
export function newestSubmitted(versions: readonly Version[]): Version | undefined {
  return versions
    .filter(isWaiting)
    .sort((a, b) => (stamp(b) ?? '').localeCompare(stamp(a) ?? ''))[0];
}

/* ------------------------------------------------------------------ */
/* The chip                                                            */
/* ------------------------------------------------------------------ */

export interface ReviewChip {
  label: string;
  /** A `pill-…` suffix that already exists in `styles.css`. */
  tone: 'uploaded' | 'processing' | 'submitted' | 'done';
  title: string;
  /** A quiet second fact, when one chip cannot hold both. */
  extra?: string;
}

/**
 * ONE CHIP PER RECORDING, decided in priority order.
 *
 * Returns null when there is honestly nothing to say — a failed row (the status
 * column already shouts about it), or a row past the lookup cap. A null chip
 * renders as an em dash, which reads as "not known" rather than as "nothing is
 * happening".
 */
export function reviewChip(r: AdminRecording, facts: ReviewFacts | undefined): ReviewChip | null {
  if (r.status === 'failed') return null;
  if (r.status === 'uploaded') {
    return {
      label: 'not started',
      tone: 'uploaded',
      title: 'The bytes were never confirmed, so no worker has picked this up.',
    };
  }
  if (r.status === 'queued' || r.status === 'processing') {
    return {
      label: 'processing',
      tone: 'processing',
      title: 'The pipeline is still making the first arrangement.',
    };
  }

  if (facts === undefined) return null;
  if (facts.at === 'loading') {
    return { label: 'checking…', tone: 'uploaded', title: 'Reading this recording’s versions.' };
  }
  if (facts.at === 'failed') return null;
  if (facts.versions.length === 0) return null;

  const waiting = submittedCount(facts.versions);

  if (truthPicked(facts.versions)) {
    return {
      label: 'truth picked',
      tone: 'done',
      title: 'A person’s arrangement is what this recording answers with.',
      ...(waiting > 0 ? { extra: `${waiting} submitted` } : {}),
    };
  }
  if (waiting > 0) {
    return {
      label: `${waiting} submitted`,
      tone: 'submitted',
      title:
        waiting === 1
          ? 'One arrangement has been handed over and is waiting for you.'
          : `${waiting} arrangements have been handed over and are waiting for you.`,
    };
  }
  return {
    label: 'in labeling',
    tone: 'uploaded',
    title: 'Out for labelling. Nobody has submitted an arrangement yet.',
  };
}

/* ------------------------------------------------------------------ */
/* Asking the server                                                   */
/* ------------------------------------------------------------------ */

export type ReviewFacts =
  | { at: 'loading' }
  | { at: 'ready'; versions: Version[] }
  | { at: 'failed' };

/** A recording that can have versions at all: the pipeline writes the segments
 *  and the first version in the same breath, so no clips means no versions, and
 *  a row still moving through the machine is described by its status alone. */
function canHaveVersions(r: AdminRecording): boolean {
  return r.segmentCount > 0 && (r.status === 'review' || r.status === 'done');
}

/**
 * The versions of every recording worth asking about, as they arrive.
 *
 * THE FAN-OUT IS NOT TIED TO THE EFFECT'S CLEANUP, which is the one thing that
 * is easy to get wrong here. The Recordings page replaces its `rows` array
 * every five seconds while anything is processing; an effect that aborted its
 * requests on cleanup would cancel and restart the whole fan-out on every poll
 * and never finish a single lookup. So the effect only ENQUEUES what it has not
 * asked about yet — `started` is the memory that makes a re-run cheap — and the
 * one AbortController belongs to the component, not to the effect.
 *
 * `refreshKey` is the deliberate forget: change it and every recording is asked
 * about again. That is how the Refresh button picks up a submission that landed
 * after the page loaded — and why callers fold the PROJECT into it, so that
 * switching projects starts from nothing instead of carrying one project's
 * answers into another's list.
 */
export function useReviewFacts(
  token: string,
  recordings: AdminRecording[] | null,
  refreshKey: string | number,
): ReadonlyMap<string, ReviewFacts> {
  const [facts, setFacts] = useState<Map<string, ReviewFacts>>(() => new Map());
  const started = useRef(new Set<string>());
  const abort = useRef<AbortController | null>(null);
  /** Answers from before a refresh are dropped rather than written over the
   *  fresh ones — they are the older truth, and they can arrive later. */
  const generation = useRef(0);

  useEffect(() => {
    return () => abort.current?.abort();
  }, []);

  useEffect(() => {
    generation.current += 1;
    started.current.clear();
    abort.current?.abort();
    abort.current = new AbortController();
    setFacts(new Map());
  }, [token, refreshKey]);

  useEffect(() => {
    if (!recordings) return;
    const signal = abort.current?.signal;
    const mine = generation.current;

    const wanted = recordings
      .filter(canHaveVersions)
      .slice(0, MAX_LOOKUPS)
      .map((r) => r.id)
      .filter((id) => !started.current.has(id));
    if (wanted.length === 0) return;

    for (const id of wanted) started.current.add(id);
    setFacts((prev) => {
      const next = new Map(prev);
      for (const id of wanted) next.set(id, { at: 'loading' });
      return next;
    });

    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < wanted.length) {
        const id = wanted[cursor++]!;
        try {
          const list = await api.versions(token, id, signal);
          if (generation.current !== mine) return;
          setFacts((prev) => new Map(prev).set(id, { at: 'ready', versions: list.versions }));
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (generation.current !== mine) return;
          /*
           * A failed lookup is a MISSING chip, never a wrong one, and never a
           * banner. The recording itself loaded; only the extra question about
           * it did not, and shouting about that would bury the twenty rows that
           * answered fine.
           */
          setFacts((prev) => new Map(prev).set(id, { at: 'failed' }));
        }
      }
    };

    void Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, wanted.length) }, () => worker()),
    );
  }, [token, recordings, refreshKey]);

  return facts;
}
